<script setup lang="ts">
/**
 * อ่านเมนูจากแชท — the recheck modal (the POS sale page "รีเช็ค" in the chat's own look). Left: what the customer
 * sent (picture with zoom/pan, or the text line by line) + a note to the AI. Right: the list the staff fix — −/+/type
 * (yellow = the AI saw no number), remove, add by name or P code, unmatched → "ค้นหาแล้วเพิ่มเอง", sort P code ↔
 * customer order, the pack (suggested from what the customer said) and a live count. Pointing at a row marks it on the
 * picture / in the text. "ยืนยันลงออเดอร์" replaces the menus of the order tab (stores/menuRead.ts confirm()).
 * Round 2: opens READY (owner 12:52) — the note to the AI is focused, the right side waits with one "อ่านเมนู" button
 * (Ctrl/Cmd+Enter in the note reads too); after a read the footer has "อ่านใหม่". "สุ่มเมนูที่เหลือ N กล่อง" under the count — type chips pre-selected from what the customer said
 * ("คละเอาแต่ไก่"), random rows marked "สุ่ม" and editable like any row, "สุ่มใหม่" re-rolls the untouched ones.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import type { PosMenu } from '../../api/pos/types'
import { boxStyle, confirmLabel, dishName, lineFor, pCodeOf, searchMenus, type RcRow } from '../../lib/menuRead'
import { useMenuRead } from '../../stores/menuRead'
import { useOrders } from '../../stores/order'
import LibDialog from '../library/LibDialog.vue'
import Icon from '../ui/Icon.vue'

const mr = useMenuRead()
const orders = useOrders()

const src = computed(() => mr.src)
const res = computed(() => mr.result)
const reading = computed(() => mr.phase === 'reading')
const ready = computed(() => mr.phase === 'ready')
// while reading: a scan line runs over what the customer sent + real seconds on the right (no invented progress)
const secs = ref(0)
let tick: ReturnType<typeof setInterval> | null = null
watch(
  reading,
  (on) => {
    if (tick) clearInterval(tick)
    tick = null
    secs.value = 0
    if (!on) return
    const t0 = Date.now()
    tick = setInterval(() => (secs.value = Math.floor((Date.now() - t0) / 1000)), 500)
  },
  { immediate: true },
)
onBeforeUnmount(() => {
  if (tick) clearInterval(tick)
})
const noteBox = ref<HTMLTextAreaElement | null>(null)
const modKey = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl'
function noteKey(e: KeyboardEvent): void {
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !reading.value) {
    e.preventDefault()
    void mr.read()
  }
}
const sets = computed(() => orders.catalog?.set_menus ?? [])
const menus = computed(() => orders.catalog?.menus ?? [])
const formPack = computed(() => {
  const f = src.value ? orders.forms[src.value.convId] : null
  return f?.setMenuId ? (sets.value.find((s) => s.id === f.setMenuId) ?? null) : null
})
const isSuggested = computed(() => mr.suggestedId !== null && mr.packId === mr.suggestedId)
const stated = computed(() => res.value?.pack?.stated ?? null)
const cands = computed(() => (res.value?.pack?.suggested ? [] : (res.value?.pack?.candidates ?? [])))
const anyGuess = computed(() => mr.rows.some((r) => r.guess))
const empty = computed(
  () => mr.phase === 'ok' && !mr.rows.length && !res.value?.unmatched.length && !res.value?.warn.length,
)
const enOf = (id: number): string | null => menus.value.find((m) => m.id === id)?.name_en || null

// ------------------------------------------------ "สุ่มเมนูที่เหลือ" (the customer said "คละ" / the pack is not full)
const noLimit = computed(() => !mr.limit)
const showFill = computed(() => mr.phase === 'ok' && (mr.missing > 0 || mr.rndBoxes > 0 || (!!res.value?.mix_rest && noLimit.value)))
const hintText = computed(() => {
  const r = res.value
  if (!r) return ''
  const said = r.mix_hint?.said ?? ''
  const note = r.mix_note && !said.includes(r.mix_note) ? r.mix_note : ''
  if (said) return `จากแชท “${said}”${note ? ` · ${note}` : ''}`
  return r.mix_rest ? `ลูกค้าให้ร้านคละเมนูที่เหลือ${note ? ` — ${note}` : ''}` : ''
})
const fillNote = computed(() => {
  if (noLimit.value) return 'เลือกแพ็กก่อน จึงรู้ว่าเหลือกี่กล่อง'
  if (!mr.pool.length) return 'แพ็กนี้ไม่มีเมนูที่สุ่มได้'
  if (!mr.fillable.length) return 'ไม่มีเมนูประเภทที่เลือกให้สุ่ม เลือกประเภทอื่น'
  return ''
})
const fillLabel = computed(() =>
  mr.missing > 0 || noLimit.value ? `สุ่มเมนูที่เหลือ${mr.missing ? ` ${mr.missing} กล่อง` : ''}` : `สุ่มใหม่ ${mr.rndBoxes} กล่อง`,
)
function doFill(): void {
  if (mr.missing > 0) mr.fill()
  else mr.refill()
}

const sub = computed(() =>
  src.value?.kind === 'image' ? 'AI อ่านจากรูป ตรวจเทียบกับรูปก่อนลงออเดอร์ · ลูกค้าไม่เห็นหน้านี้' : 'AI อ่านจากข้อความ ตรวจเทียบทีละบรรทัดก่อนลงออเดอร์ · ลูกค้าไม่เห็นหน้านี้',
)

function onPack(e: Event): void {
  const v = (e.target as HTMLSelectElement).value
  mr.packId = v ? Number(v) : null
}

// ------------------------------------------------ rows
function typed(r: RcRow, e: Event): void {
  const el = e.target as HTMLInputElement
  mr.setQty(r.menuId, el.value.replace(/\D/g, '') || '1')
  el.value = String(mr.rows.find((x) => x.menuId === r.menuId)?.qty ?? 1)
}

// ------------------------------------------------ add a menu
const q = ref('')
const qFocus = ref(false)
const addInput = ref<HTMLInputElement | null>(null)
const hits = computed<PosMenu[]>(() => searchMenus(menus.value, q.value))
function pick(m: PosMenu): void {
  mr.add(m)
  q.value = ''
  hover.value = m.id
  void nextTick(() => document.querySelector(`[data-rc-row="${m.id}"]`)?.scrollIntoView({ block: 'nearest' }))
}
function find(u: string): void {
  q.value = u.replace(/\.$/, '')
  addInput.value?.focus()
}

// ------------------------------------------------ pointing: row ↔ picture box / text line
const hover = ref<number | null>(null)
const lines = computed(() => (src.value?.kind === 'text' ? src.value.texts.join('\n').split('\n').filter((l) => l.trim()) : []))
const hoverRow = computed(() => mr.rows.find((r) => r.menuId === hover.value) ?? null)
const hlBox = computed(() => hoverRow.value?.box ?? null)
// the pointed box stays sharp on a copy of the picture; the rest is blurred + darkened (owner 18:31)
function clipOf(b: [number, number, number, number]): string {
  const [y0, x0, y1, x1] = b
  return `inset(${y0 / 10}% ${100 - x1 / 10}% ${100 - y1 / 10}% ${x0 / 10}% round 6px)`
}
const hlLine = computed(() => (hoverRow.value ? lineFor(hoverRow.value, lines.value, enOf(hoverRow.value.menuId)) : -1))
const lineHover = ref(-1)
const rowForLine = computed(() => {
  if (lineHover.value < 0) return null
  return mr.rows.find((r) => lineFor(r, lines.value, enOf(r.menuId)) === lineHover.value)?.menuId ?? null
})

// ------------------------------------------------ picture: fit width, zoom (buttons / ctrl+wheel), pan (drag / wheel)
const stage = ref<HTMLElement | null>(null)
const img = ref<HTMLImageElement | null>(null)
const sw = ref(0)
const sh = ref(0)
const ih = ref(0) // picture height at fit width
const s = ref(1)
const tx = ref(0)
const ty = ref(0)
const broken = ref(false)

function measure(): void {
  const st = stage.value
  const im = img.value
  if (!st || !im || !im.naturalWidth) return
  sw.value = st.clientWidth
  sh.value = st.clientHeight
  ih.value = (sw.value * im.naturalHeight) / im.naturalWidth
}
function clamp(): void {
  const w = sw.value * s.value
  const h = ih.value * s.value
  tx.value = w <= sw.value ? (sw.value - w) / 2 : Math.min(0, Math.max(sw.value - w, tx.value))
  ty.value = h <= sh.value ? 0 : Math.min(0, Math.max(sh.value - h, ty.value))
}
function fit(): void {
  measure()
  s.value = 1
  tx.value = 0
  ty.value = 0
  clamp()
}
function zoom(f: number, cx = sw.value / 2, cy = sh.value / 2): void {
  const n = Math.min(5, Math.max(0.4, s.value * f))
  tx.value = cx - ((cx - tx.value) * n) / s.value
  ty.value = cy - ((cy - ty.value) * n) / s.value
  s.value = n
  clamp()
}
function onWheel(e: WheelEvent): void {
  if (e.ctrlKey || e.metaKey) {
    const r = stage.value?.getBoundingClientRect()
    zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15, r ? e.clientX - r.left : undefined, r ? e.clientY - r.top : undefined)
  } else {
    tx.value -= e.deltaX
    ty.value -= e.deltaY
    clamp()
  }
}
let drag: { x: number; y: number; tx: number; ty: number } | null = null
function down(e: PointerEvent): void {
  if (e.button !== 0) return
  drag = { x: e.clientX, y: e.clientY, tx: tx.value, ty: ty.value }
  ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
}
function move(e: PointerEvent): void {
  if (!drag) return
  tx.value = drag.tx + e.clientX - drag.x
  ty.value = drag.ty + e.clientY - drag.y
  clamp()
}
function up(): void {
  drag = null
}
const canvas = computed(() => ({ width: `${sw.value || 0}px`, transform: `translate(${tx.value}px, ${ty.value}px) scale(${s.value})` }))
// pointing at a row whose box is out of view brings it into view
watch(hlBox, (b) => {
  if (!b || !ih.value) return
  const top = (b[0] / 1000) * ih.value * s.value + ty.value
  const bottom = (b[2] / 1000) * ih.value * s.value + ty.value
  if (top < 8 || bottom > sh.value - 8) {
    ty.value = sh.value / 2 - (((b[0] + b[2]) / 2000) * ih.value * s.value)
    clamp()
  }
})
watch(
  () => src.value?.anchorId,
  (id) => {
    broken.value = false
    hover.value = null
    q.value = ''
    // ready: the note first (the staff may tell the AI something before it reads)
    if (id) void nextTick(() => noteBox.value?.focus())
  },
  { immediate: true },
)
</script>

<template>
  <LibDialog v-if="src" title="อ่านเมนูจากแชท" :sub="sub" :width="1180" :close-on-backdrop="false" @close="mr.close()">
    <div class="rc">
      <!-- ============================================ what the customer sent -->
      <section class="rc-l" :aria-label="src.kind === 'image' ? 'รูปที่ลูกค้าส่ง' : 'ข้อความลูกค้า'">
        <div class="rc-h">
          <span class="cap">{{ src.kind === 'image' ? 'รูปที่ลูกค้าส่ง' : `ข้อความลูกค้า${src.ids.length > 1 ? ` (${src.ids.length} ข้อความ)` : ''}` }}</span>
          <span class="grow"></span>
          <template v-if="src.kind === 'image' && src.imageUrl && !broken">
            <button type="button" class="zb" aria-label="ย่อรูป" title="ย่อ" @click="zoom(1 / 1.25)"><Icon name="zoomOut" /></button>
            <span class="zv mono" aria-live="polite">{{ Math.round(s * 100) }}%</span>
            <button type="button" class="zb" aria-label="ขยายรูป" title="ขยาย (Ctrl + เลื่อนลูกกลิ้ง)" @click="zoom(1.25)"><Icon name="zoomIn" /></button>
            <button type="button" class="zb" aria-label="พอดีกรอบ" title="พอดีกรอบ" @click="fit"><Icon name="scan" /></button>
          </template>
        </div>

        <div
          v-if="src.kind === 'image' && src.imageUrl && !broken"
          ref="stage"
          class="stage"
          @wheel.prevent="onWheel"
          @pointerdown="down"
          @pointermove="move"
          @pointerup="up"
          @pointercancel="up"
        >
          <div class="cv" :style="canvas">
            <img
              ref="img"
              :src="src.imageUrl"
              :class="{ fade: !!hlBox }"
              alt="รูปที่ลูกค้าส่ง"
              draggable="false"
              @load="fit"
              @error="broken = true"
            />
            <!-- always mounted so it is decoded once — a copy made on hover shows blank for a moment -->
            <img
              class="sharp"
              :src="src.imageUrl"
              alt=""
              draggable="false"
              aria-hidden="true"
              :style="hlBox ? { clipPath: clipOf(hlBox) } : { visibility: 'hidden' }"
            />
            <span v-if="hlBox" class="box-hl" :style="boxStyle(hlBox)" aria-hidden="true"></span>
          </div>
          <span v-if="reading" class="scan" aria-hidden="true"></span>
        </div>
        <p v-else-if="src.kind === 'image'" class="noimg"><Icon name="image" />แสดงรูปที่นี่ไม่ได้ ดูรูปในแชทแทน (AI ยังอ่านจากรูปต้นฉบับได้)</p>
        <div v-else class="lw">
          <ol class="lines scroll">
            <li
              v-for="(l, i) in lines"
              :key="i"
              class="ln"
              :class="{ on: hlLine === i || lineHover === i }"
              @mouseenter="lineHover = i"
              @mouseleave="lineHover = -1"
            >
              {{ l }}
            </li>
          </ol>
          <span v-if="reading" class="scan" aria-hidden="true"></span>
        </div>

        <p v-if="src.kind === 'image' && src.texts.length" class="ctx">
          <span class="ctx-l">ข้อความที่ลูกค้าส่งตามมา (ส่งให้ AI ด้วย)</span>
          <span v-for="(t, i) in src.texts" :key="i" class="ctx-t">{{ t }}</span>
        </p>
        <label class="nt">
          <span class="nt-l">หมายเหตุถึง AI <span class="mut">(ใส่ก่อนกดอ่าน · ไม่บังคับ)</span></span>
          <textarea
            ref="noteBox"
            v-model="mr.note"
            rows="2"
            maxlength="1000"
            placeholder="เช่น เอาเฉพาะที่ติ๊ก · อย่างละ 2 · ที่ขีดฆ่าคือไม่เอา"
            @keydown="noteKey"
          ></textarea>
        </label>
      </section>

      <!-- ============================================ the list -->
      <section class="rc-r" aria-label="รายการที่อ่านได้">
        <div v-if="reading" class="topbar" role="progressbar" aria-label="กำลังอ่านเมนู"><span></span></div>

        <div class="pk">
          <span class="pk-l">แพ็ก</span>
          <span v-if="mr.cont" class="pk-fixed"><Icon name="repeat" />{{ mr.cont.name }} · เหลือ {{ mr.cont.left }} มื้อ</span>
          <select
            v-else
            class="pk-s"
            :class="{ sug: isSuggested, need: !mr.packId && mr.phase === 'ok' }"
            :value="mr.packId ?? ''"
            aria-label="แพ็ก"
            @change="onPack"
          >
            <option value="">เลือกแพ็ก…</option>
            <option v-for="p in sets" :key="p.id" :value="p.id">{{ p.name }} · {{ p.meals }} มื้อ</option>
          </select>
          <span v-if="isSuggested" class="sug-chip" title="จากที่ลูกค้าบอกในแชท ตรวจก่อนยืนยัน"><Icon name="sparkles" />แนะนำจากแชท</span>
        </div>
        <p v-if="stated && (isSuggested || cands.length)" class="pk-note">
          ลูกค้าบอก <b>{{ stated.text ?? `${stated.boxes} กล่อง` }}</b>
          <template v-if="isSuggested && formPack && formPack.id !== mr.packId"> · ในแท็บลงออเดอร์เลือก {{ formPack.name }} ไว้ ยืนยันแล้วจะเปลี่ยนเป็นแพ็กนี้</template>
        </p>
        <div v-if="cands.length > 1" class="pk-c">
          <span class="pk-cl">แพ็กที่มี {{ cands[0]?.meals }} มื้อ</span>
          <button v-for="c in cands" :key="c.id" type="button" class="pk-cb" :class="{ on: mr.packId === c.id }" @click="mr.packId = c.id">
            {{ c.name }}
          </button>
        </div>
        <p v-else-if="!mr.cont && !mr.packId && mr.phase === 'ok'" class="pk-note warn">เลือกแพ็กก่อน แล้วค่อยยืนยันลงออเดอร์</p>

        <div v-if="ready" class="go">
          <p class="go-t">ยังไม่ได้อ่าน</p>
          <p class="go-h">
            ถ้ามีอะไรที่ AI ต้องรู้ก่อน พิมพ์ในหมายเหตุทางซ้าย<template v-if="!mr.cont"> และเลือกแพ็กได้เลย</template> แล้วกดอ่าน
          </p>
          <button type="button" class="b1 go-b" @click="mr.read()"><Icon name="sparkles" />อ่านเมนู</button>
          <span class="go-k"><kbd>{{ modKey }}</kbd> + <kbd>Enter</kbd> ในช่องหมายเหตุ ก็อ่านได้</span>
        </div>
        <div v-else class="sm" :class="`lv-${mr.line.level}`">
          <Icon :name="mr.line.level === 'ok' ? 'checkCircle' : mr.line.level === 'warn' ? 'alert' : 'info'" />
          <span class="sm-t" aria-live="polite">{{ mr.line.text }}</span>
          <span class="grow"></span>
          <button type="button" class="srt" :disabled="mr.rows.length < 2" @click="mr.toggleSort()">
            <Icon name="sort" />{{ mr.sort === 'p' ? 'เรียงตามที่ลูกค้าลิสต์' : 'เรียงตามรหัส P' }}
          </button>
        </div>

        <div v-if="showFill" class="rf" :class="{ asked: !!hintText }">
          <div class="rf-h">
            <button
              type="button"
              class="rf-b"
              :disabled="!!fillNote"
              :title="`สุ่มจากเมนูที่ยังไม่มีในรายการก่อน กระจายเท่าๆ กัน ไม่รวมของว่าง${mr.withPlus ? '' : ' และเมนูบวกเงิน'} (แบบปุ่มสุ่มหน้าขาย POS)`"
              @click="doFill"
            >
              <Icon name="shuffle" />{{ fillLabel }}
            </button>
            <span v-if="hintText" class="rf-hint">{{ hintText }}</span>
          </div>
          <div v-if="mr.kinds.length" class="rf-c" role="group" aria-label="ประเภทที่สุ่มได้">
            <span class="rf-cl">สุ่มจาก</span>
            <button type="button" class="chip" :class="{ on: mr.allow === null }" :aria-pressed="mr.allow === null" @click="mr.toggleKind(null)">
              ทุกประเภท
            </button>
            <button
              v-for="k in mr.kinds"
              :key="k.k"
              type="button"
              class="chip"
              :class="{ on: !!mr.allow?.includes(k.k) }"
              :aria-pressed="!!mr.allow?.includes(k.k)"
              @click="mr.toggleKind(k.k)"
            >
              {{ k.label }} <span class="n">{{ k.n }}</span>
            </button>
            <button
              type="button"
              class="chip"
              :class="{ on: mr.withPlus }"
              :aria-pressed="mr.withPlus"
              title="เปิดเมื่อลูกค้ายอมจ่ายเพิ่มสำหรับเมนูบวกเงิน"
              @click="mr.withPlus = !mr.withPlus"
            >
              รวมเมนูบวกเงิน
            </button>
          </div>
          <p v-if="fillNote" class="rf-note">{{ fillNote }}</p>
        </div>

        <p v-if="mr.phase === 'error'" class="al crit" role="alert">
          <Icon name="alertCircle" /><span>{{ mr.error }}</span>
          <button type="button" class="lnk" @click="mr.read()">ลองใหม่</button>
        </p>
        <div v-else-if="reading && !mr.rows.length" class="rd">
          <span class="rd-ic" aria-hidden="true"><Icon name="sparkles" /></span>
          <p class="rd-t" role="status">กำลังอ่านเมนูจาก{{ src.kind === 'image' ? 'รูป' : 'ข้อความ' }}</p>
          <p class="rd-h">{{ secs > 10 ? 'นานกว่าปกติ ยังอ่านอยู่ รออีกสักครู่' : 'ปกติใช้เวลา 3–10 วินาที' }}</p>
          <p class="rd-s" aria-hidden="true">{{ secs ? `ผ่านไป ${secs} วินาที` : '\u00a0' }}</p>
        </div>
        <ul v-if="res && (res.warn.length || res.unmatched.length || (res.mix_rest && !showFill))" class="als">
          <li v-for="w in res.warn" :key="`w${w}`" class="al"><Icon name="alert" /><span>{{ w }}</span></li>
          <li v-for="u in res.unmatched" :key="`u${u}`" class="al">
            <Icon name="helpCircle" /><span>"{{ u }}" หาไม่เจอ</span>
            <button type="button" class="lnk" @click="find(u)">ค้นหาแล้วเพิ่มเอง</button>
          </li>
          <li v-if="res.mix_rest && !showFill" class="al">
            <Icon name="info" /><span>ลูกค้าให้ร้านคละเมนูที่เหลือ<template v-if="res.mix_note"> — {{ res.mix_note }}</template></span>
          </li>
        </ul>
        <p v-if="empty" class="wait">ไม่พบรายการเมนูใน{{ src.kind === 'image' ? 'รูป' : 'ข้อความ' }}นี้ เพิ่มเองได้จากช่องด้านล่าง</p>

        <ul v-if="mr.rows.length" class="rows scroll" :class="{ dim: reading }" aria-label="เมนูที่จะลงออเดอร์">
          <li
            v-for="r in mr.rows"
            :key="r.menuId"
            class="row"
            :class="{ on: hover === r.menuId || rowForLine === r.menuId, boxed: !!r.box }"
            :data-rc-row="r.menuId"
            @mouseenter="hover = r.menuId"
            @mouseleave="hover = null"
          >
            <span class="stp">
              <button type="button" class="stp-b" :disabled="r.qty <= 1" :aria-label="`ลด ${r.name}`" @click="mr.setQty(r.menuId, r.qty - 1)">
                <Icon name="minus" />
              </button>
              <input
                class="stp-v"
                :class="{ guess: r.guess }"
                type="text"
                inputmode="numeric"
                :value="r.qty"
                :aria-label="`จำนวน ${r.name}`"
                :title="r.guess ? 'AI ไม่เห็นจำนวน ใส่ 1 ไว้ก่อน ตรวจด้วย' : undefined"
                @change="typed(r, $event)"
                @focus="($event.target as HTMLInputElement).select()"
              />
              <button type="button" class="stp-b" :aria-label="`เพิ่ม ${r.name}`" @click="mr.setQty(r.menuId, r.qty + 1)"><Icon name="plus" /></button>
            </span>
            <span class="nm">
              <span v-if="r.p" class="code mono">{{ r.p }}</span><span class="nm-t">{{ dishName(r.name) }}</span>
              <span v-if="r.snack" class="tag">ของว่าง ไม่นับในแพ็ก</span>
              <span v-if="r.rnd" class="tag rnd">{{ r.rnd >= r.qty ? 'สุ่ม' : `สุ่ม +${r.rnd}` }}</span>
            </span>
            <button type="button" class="rm" :aria-label="`เอา ${r.name} ออก`" title="เอาออก" @click="mr.remove(r.menuId)"><Icon name="x" /></button>
          </li>
        </ul>
        <span v-else-if="!ready" class="grow" aria-hidden="true"></span>
        <p v-if="anyGuess" class="legend"><span class="sw" aria-hidden="true"></span>ช่องเหลือง = AI ไม่เห็นจำนวน ใส่ 1 ไว้ก่อน ตรวจกับที่ลูกค้าส่ง</p>

        <div v-if="!ready" class="add">
          <label class="add-i"
            ><Icon name="plus" /><input
              ref="addInput"
              v-model="q"
              type="search"
              autocomplete="off"
              aria-label="เพิ่มเมนู"
              placeholder="เพิ่มเมนู — พิมพ์ชื่อหรือรหัส P"
              @focus="qFocus = true"
              @blur="qFocus = false"
              @keydown.enter.prevent="hits[0] && pick(hits[0])"
              @keydown.esc.stop="q = ''"
          /></label>
          <ul v-if="q.trim() && qFocus" class="dd" role="listbox" aria-label="เมนูที่ตรง">
            <li v-for="m in hits" :key="m.id">
              <button type="button" class="dd-o" @mousedown.prevent="pick(m)">
                <span v-if="pCodeOf(m.name)" class="code mono">{{ pCodeOf(m.name) }}</span>{{ dishName(m.name) }}
              </button>
            </li>
            <li v-if="!hits.length" class="dd-none">ไม่พบเมนูที่ตรง</li>
          </ul>
        </div>
      </section>
    </div>

    <template #footer>
      <button type="button" class="b2" @click="mr.close()">ปิด</button>
      <span class="grow"></span>
      <span v-if="mr.replacing && mr.phase === 'ok'" class="rep"><Icon name="info" />จะแทนเมนูที่เลือกไว้ {{ mr.replacing }} เมนู</span>
      <button v-if="!ready" type="button" class="b2" :disabled="reading" @click="mr.read()"><Icon name="refresh" />อ่านใหม่</button>
      <button type="button" class="b1" :disabled="!mr.canConfirm" @click="mr.confirm()"><Icon name="check" />{{ confirmLabel(mr.counts) }}</button>
    </template>
  </LibDialog>
</template>

<style scoped>
.rc {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.12fr);
  gap: 20px;
  height: min(660px, calc(100vh - 210px));
  min-height: 420px;
}
.grow {
  flex: 1;
}
.mono {
  font-family: var(--mono);
}
.mut {
  color: var(--mut);
  font-weight: 400;
}

/* ---------------- left: the source */
.rc-l {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}
.rc-h {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
}
.cap {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.zb {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--mut);
  border: 1px solid var(--line);
  background: #fff;
}
.zb:hover {
  color: var(--ink);
  background: #f3f6f4;
}
.zb :deep(.ic) {
  width: 16px;
  height: 16px;
}
.zv {
  min-width: 44px;
  text-align: center;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.stage {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #f4f6f5;
  cursor: grab;
  touch-action: none;
}
.stage:active {
  cursor: grabbing;
}
.cv {
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
}
.cv img {
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
  pointer-events: none;
  transition: filter 0.16s ease;
}
.cv img.fade {
  filter: blur(2.5px) brightness(0.5) saturate(0.6);
}
.cv img.sharp {
  position: absolute;
  left: 0;
  top: 0;
  transition: none;
}
/* not .hl — styles/health.css owns that global class (it painted a solid background here) */
.box-hl {
  position: absolute;
  border: 3px solid var(--ac2);
  border-radius: 6px;
  box-shadow:
    0 0 0 2px #fff,
    0 8px 22px rgba(0, 0, 0, 0.45);
  pointer-events: none;
}
.scan {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-bottom: 2px solid var(--ac);
  background: linear-gradient(to bottom, rgba(14, 122, 92, 0) 70%, rgba(14, 122, 92, 0.2));
  animation: rc-scan 2.2s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}
@keyframes rc-scan {
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  12%,
  88% {
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    opacity: 0;
  }
}
.lw {
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-radius: 12px;
}
.noimg {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 16px;
  border: 1px dashed var(--line);
  border-radius: 12px;
  font-size: 13px;
  line-height: 20px;
  color: var(--mut);
}
.noimg :deep(.ic) {
  width: 18px;
  height: 18px;
  flex: none;
}
.lines {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.ln {
  padding: 5px 14px;
  font-size: 14px;
  line-height: 22px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  border-left: 3px solid transparent;
}
.ln.on {
  background: var(--acs);
  border-left-color: var(--ac);
}
.ctx {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 8px 12px;
  border-radius: 10px;
  background: #f3f6f4;
}
.ctx-l {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.ctx-t {
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
}
.nt {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.nt-l {
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
}
.nt textarea {
  resize: none;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  font: inherit;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  background: #fff;
  outline: none;
}
.nt textarea:focus {
  border-color: var(--ac);
}

/* ---------------- right: the list */
.rc-r {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}
.topbar {
  position: absolute;
  left: 0;
  right: 0;
  top: -16px;
  height: 3px;
  overflow: hidden;
  border-radius: 2px;
  background: var(--acs);
}
.topbar span {
  position: absolute;
  inset: 0 auto 0 0;
  width: 36%;
  background: var(--ac);
  animation: rc-bar 1.1s ease-in-out infinite;
}
@keyframes rc-bar {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(280%);
  }
}
.pk {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pk-l {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  flex: none;
}
.pk-s {
  flex: 1 1 auto;
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  font: inherit;
  font-size: 13.5px;
  line-height: 20px;
  color: var(--ink);
  outline: none;
}
.pk-s:focus {
  border-color: var(--ac);
}
.pk-s.sug {
  border-color: #9fcdb8;
  background: #f5faf7;
}
.pk-s.need {
  border-color: #e7c77a;
  background: #fffaf0;
}
.pk-fixed {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ac2);
}
.pk-fixed :deep(.ic) {
  width: 15px;
  height: 15px;
}
.sug-chip {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 9px;
  border-radius: 999px;
  background: var(--acs);
  color: var(--ac2);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.sug-chip :deep(.ic) {
  width: 13px;
  height: 13px;
}
.pk-note {
  margin: -2px 0 0 36px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.pk-note.warn {
  color: var(--warn);
}
.pk-c {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-left: 36px;
}
.pk-cl {
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.pk-cb {
  min-height: 28px;
  padding: 3px 10px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  font-size: 12.5px;
  line-height: 19px;
}
.pk-cb:hover,
.pk-cb.on {
  border-color: #9fcdb8;
  background: var(--acs);
  color: var(--ac2);
}
.sm {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 6px 8px 6px 12px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
}
.sm > :deep(.ic) {
  width: 17px;
  height: 17px;
  flex: none;
}
.lv-ok {
  background: var(--acs);
  color: var(--ac2);
}
.lv-warn {
  background: var(--warns);
  color: var(--warn);
}
.lv-none {
  background: #eef2f0;
  color: #3e4d46;
}
.sm-t {
  min-width: 0;
}
.srt {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 9px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.75);
  color: var(--ink);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 19px;
  white-space: nowrap;
}
.srt:hover:not(:disabled) {
  background: #fff;
}
.srt:disabled {
  opacity: 0.55;
  cursor: default;
}
.srt :deep(.ic) {
  width: 14px;
  height: 14px;
}
.go {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 0;
  padding: 24px 20px;
  border: 1px dashed var(--line);
  border-radius: 12px;
  text-align: center;
}
.go-t {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 23px;
}
.go-h {
  margin: 0;
  max-width: 340px;
  font-size: 13px;
  line-height: 20px;
  color: var(--mut);
}
.go-b {
  margin-top: 6px;
  height: 42px;
  padding: 0 22px;
  font-size: 14px;
}
.go-k {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.go-k kbd {
  display: inline-block;
  padding: 0 5px;
  border: 1px solid var(--line);
  border-bottom-width: 2px;
  border-radius: 5px;
  background: #fff;
  font: 500 12px/18px var(--mono);
  color: var(--ink);
}
.rf {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fafcfb;
}
.rf.asked {
  border-color: #bfdccd;
  background: #f5faf7;
}
.rf-h {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
}
.rf-b {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px 0 10px;
  border: 1px solid #9fcdb8;
  border-radius: 8px;
  background: #fff;
  color: var(--ac2);
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.rf-b:hover:not(:disabled) {
  background: var(--acs);
}
.rf-b:disabled {
  border-color: var(--line);
  color: #8a9892;
  cursor: default;
}
.rf-b :deep(.ic) {
  width: 15px;
  height: 15px;
}
.rf-hint {
  min-width: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--ac2);
  overflow-wrap: anywhere;
}
.rf-c {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.rf-cl {
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 28px;
  padding: 3px 10px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--ink);
}
.chip:hover {
  border-color: #9fcdb8;
}
.chip.on {
  border-color: var(--ac);
  background: var(--acs);
  color: var(--ac2);
  font-weight: 600;
}
.chip .n {
  font-family: var(--mono);
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.rf-note {
  margin: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--warn);
}
.tag.rnd {
  color: var(--ac2);
  font-weight: 600;
}
.als {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 132px;
  overflow-y: auto;
  flex: none;
}
.al {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 0;
  padding: 6px 10px;
  border-radius: 8px;
  background: #fffaf0;
  color: #5c3b00;
  font-size: 12.5px;
  line-height: 19px;
}
.al :deep(.ic) {
  width: 14px;
  height: 14px;
  flex: none;
  margin-top: 2.5px;
  color: var(--warn);
}
.al.crit {
  background: var(--crits);
  color: #8c1c12;
}
.al.crit :deep(.ic) {
  color: var(--crit);
}
.lnk {
  flex: none;
  color: var(--ac2);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
}
.rd {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 0;
  padding: 24px 20px;
  border: 1px dashed var(--line);
  border-radius: 12px;
  text-align: center;
}
.rd-ic {
  position: relative;
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  margin-bottom: 6px;
  border-radius: 50%;
  background: var(--acs);
  color: var(--ac);
}
.rd-ic::before {
  content: '';
  position: absolute;
  inset: -5px;
  border: 2px solid transparent;
  border-top-color: var(--ac);
  border-right-color: var(--ac);
  border-radius: 50%;
  animation: rc-spin 0.9s linear infinite;
}
.rd-ic :deep(.ic) {
  width: 24px;
  height: 24px;
  animation: rc-pulse 1.4s ease-in-out infinite;
}
@keyframes rc-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes rc-pulse {
  50% {
    transform: scale(0.86);
    opacity: 0.7;
  }
}
.rd-t {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 23px;
}
.rd-h {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--mut);
}
.rd-s {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  font-variant-numeric: tabular-nums;
}
.wait {
  margin: 0;
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  line-height: 20px;
  color: var(--mut);
}
.rows {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--line);
  border-radius: 12px;
}
.rows.dim {
  opacity: 0.55;
}
.row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 46px;
  padding: 6px 8px 6px 10px;
}
.row + .row {
  border-top: 1px dashed #e3eae6;
}
.row.on {
  background: #f5faf7;
}
.stp {
  flex: none;
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.stp-b {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ac2);
}
.stp-b:hover:not(:disabled) {
  background: var(--acs);
}
.stp-b:disabled {
  color: #b5c2bc;
}
.stp-b :deep(.ic) {
  width: 14px;
  height: 14px;
  stroke-width: 2.2;
}
.stp-v {
  width: 40px;
  height: 30px;
  border: 0;
  border-left: 1px solid var(--line2);
  border-right: 1px solid var(--line2);
  text-align: center;
  font: 600 13.5px/20px var(--mono);
  color: var(--ink);
  outline: none;
  background: transparent;
}
.stp-v:focus {
  background: #f5faf7;
}
.stp-v.guess {
  background: #fff3c4;
  color: #6b4300;
}
.nm {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 7px;
  font-size: 14px;
  line-height: 21px;
}
.code {
  flex: none;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: var(--ac2);
}
.nm-t {
  overflow-wrap: anywhere;
}
.tag {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.rm {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #8a9892;
}
.rm:hover {
  background: var(--crits);
  color: var(--crit);
}
.rm :deep(.ic) {
  width: 15px;
  height: 15px;
}
.legend {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.sw {
  width: 14px;
  height: 12px;
  border-radius: 3px;
  background: #fff3c4;
  border: 1px solid #e7c77a;
}
.add {
  position: relative;
  flex: none;
}
.add-i {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  color: var(--mut);
}
.add-i:focus-within {
  border-color: var(--ac);
}
.add-i :deep(.ic) {
  width: 16px;
  height: 16px;
  flex: none;
}
.add-i input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 13.5px;
  line-height: 20px;
  color: var(--ink);
}
.dd {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 4px);
  z-index: 2;
  margin: 0;
  padding: 4px;
  list-style: none;
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(12, 18, 15, 0.16);
}
.dd-o {
  display: flex;
  align-items: baseline;
  gap: 7px;
  width: 100%;
  padding: 7px 10px;
  border-radius: 7px;
  font-size: 13.5px;
  line-height: 21px;
  text-align: left;
}
.dd-o:hover {
  background: var(--acs);
}
.dd-none {
  padding: 8px 10px;
  font-size: 13px;
  line-height: 20px;
  color: var(--mut);
}

/* ---------------- footer */
.b1,
.b2 {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.b1 {
  background: var(--ac);
  color: #fff;
}
.b1:hover:not(:disabled) {
  background: var(--ac2);
}
.b1:disabled {
  background: #a9c9bb;
  cursor: default;
}
.b2 {
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
}
.b2:hover:not(:disabled) {
  background: #f3f6f4;
}
.b2:disabled {
  opacity: 0.6;
  cursor: default;
}
.b1 :deep(.ic),
.b2 :deep(.ic) {
  width: 16px;
  height: 16px;
}
.rep {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--warn);
}
.rep :deep(.ic) {
  width: 14px;
  height: 14px;
}
@media (prefers-reduced-motion: reduce) {
  .topbar span {
    animation: none;
    width: 100%;
    opacity: 0.5;
  }
  .scan {
    display: none;
  }
  .rd-ic::before,
  .rd-ic :deep(.ic) {
    animation: none;
  }
}
@media (max-width: 760px) {
  .rc {
    grid-template-columns: 1fr;
    height: auto;
  }
  .stage {
    height: 320px;
    flex: none;
  }
}
</style>
